// Playground Service
// Handles chat completions via /v1/chat/completions API with API key auth

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ChatMessage {
	role: "system" | "user" | "assistant";
	content: string;
}

export interface ChatCompletionParams {
	temperature?: number;
	max_tokens?: number;
	top_p?: number;
	top_k?: number;
	stream?: boolean;
	nova?: {
		skipCache?: boolean;
		modelConfigId?: string;
		systemPromptOverride?: string;
	};
}

export interface ChatCompletionUsage {
	prompt_tokens: number;
	completion_tokens: number;
	total_tokens: number;
}

export interface ChatCompletionChoice {
	index: number;
	message: ChatMessage;
	finish_reason: string;
}

export interface ChatCompletionResponse {
	id: string;
	object: "chat.completion";
	created: number;
	model: string;
	choices: ChatCompletionChoice[];
	usage: ChatCompletionUsage;
	nova?: {
		cached: boolean;
		cache_layer: "hot" | "semantic" | null;
		response_time_ms: number;
		saved_tokens: number;
	};
}

export interface ChatCompletionChunk {
	id: string;
	object: "chat.completion.chunk";
	created: number;
	model: string;
	choices: {
		index: number;
		delta: { role?: string; content?: string };
		finish_reason: string | null;
	}[];
}

export interface StreamCallbacks {
	onToken: (token: string) => void;
	onComplete: (fullResponse: string, usage?: ChatCompletionUsage) => void;
	onError: (error: Error) => void;
}

export interface PlaygroundMessage {
	id: string;
	role: "user" | "assistant" | "system";
	content: string;
	timestamp: Date;
	tokenUsage?: ChatCompletionUsage;
	responseTimeMs?: number;
	cached?: boolean;
	cacheLayer?: "hot" | "semantic" | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL =
	import.meta.env.VITE_API_BASE_URL ||
	import.meta.env.VITE_API_URL ||
	"http://localhost:3000";

// ═══════════════════════════════════════════════════════════════════════════
// CHAT COMPLETION FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Send a non-streaming chat completion request
 */
export async function sendChatCompletion(
	apiKey: string,
	model: string,
	messages: ChatMessage[],
	params?: ChatCompletionParams,
): Promise<ChatCompletionResponse> {
	const response = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${apiKey}`,
		},
		body: JSON.stringify({
			model,
			messages,
			stream: false,
			...params,
		}),
	});

	if (!response.ok) {
		const error = await response
			.json()
			.catch(() => ({ error: { message: "Request failed" } }));
		throw new Error(
			error.error?.message || `HTTP ${response.status}: ${response.statusText}`,
		);
	}

	return response.json();
}

/**
 * Send a streaming chat completion request
 * Returns an AbortController for cancellation
 */
export function streamChatCompletion(
	apiKey: string,
	model: string,
	messages: ChatMessage[],
	params: ChatCompletionParams | undefined,
	callbacks: StreamCallbacks,
): AbortController {
	const controller = new AbortController();

	(async () => {
		try {
			const response = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${apiKey}`,
				},
				body: JSON.stringify({
					model,
					messages,
					stream: true,
					...params,
				}),
				signal: controller.signal,
			});

			if (!response.ok) {
				const error = await response
					.json()
					.catch(() => ({ error: { message: "Request failed" } }));
				throw new Error(
					error.error?.message ||
						`HTTP ${response.status}: ${response.statusText}`,
				);
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error("No response body");
			}

			const decoder = new TextDecoder();
			let fullContent = "";
			let buffer = "";

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const lines = buffer.split("\n");
				buffer = lines.pop() || "";

				for (const line of lines) {
					const trimmed = line.trim();
					if (!trimmed || trimmed === "data: [DONE]") continue;
					if (!trimmed.startsWith("data: ")) continue;

					try {
						const chunk: ChatCompletionChunk = JSON.parse(trimmed.slice(6));
						const content = chunk.choices[0]?.delta?.content;
						if (content) {
							fullContent += content;
							callbacks.onToken(content);
						}
					} catch {
						// Skip malformed JSON
					}
				}
			}

			callbacks.onComplete(fullContent);
		} catch (error: any) {
			if (error.name !== "AbortError") {
				callbacks.onError(error);
			}
		}
	})();

	return controller;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONVERSATION HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
	return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Create a user message
 */
export function createUserMessage(content: string): PlaygroundMessage {
	return {
		id: generateMessageId(),
		role: "user",
		content,
		timestamp: new Date(),
	};
}

/**
 * Create an assistant message (for streaming, content starts empty)
 */
export function createAssistantMessage(
	content: string = "",
): PlaygroundMessage {
	return {
		id: generateMessageId(),
		role: "assistant",
		content,
		timestamp: new Date(),
	};
}

/**
 * Convert PlaygroundMessages to ChatMessages for API
 */
export function toApiMessages(
	messages: PlaygroundMessage[],
	systemPrompt?: string,
): ChatMessage[] {
	const apiMessages: ChatMessage[] = [];

	if (systemPrompt) {
		apiMessages.push({ role: "system", content: systemPrompt });
	}

	for (const msg of messages) {
		if (msg.role === "user" || msg.role === "assistant") {
			apiMessages.push({ role: msg.role, content: msg.content });
		}
	}

	return apiMessages;
}

// ═══════════════════════════════════════════════════════════════════════════
// LOCAL STORAGE PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════════

const STORAGE_KEY_PREFIX = "nova_playground_";

/**
 * Save conversation to localStorage
 */
export function saveConversation(
	modelConfigId: string,
	messages: PlaygroundMessage[],
): void {
	try {
		const key = `${STORAGE_KEY_PREFIX}${modelConfigId}`;
		localStorage.setItem(key, JSON.stringify(messages));
	} catch {
		// Storage might be full or unavailable
	}
}

/**
 * Load conversation from localStorage
 */
export function loadConversation(modelConfigId: string): PlaygroundMessage[] {
	try {
		const key = `${STORAGE_KEY_PREFIX}${modelConfigId}`;
		const stored = localStorage.getItem(key);
		if (!stored) return [];

		const messages = JSON.parse(stored) as PlaygroundMessage[];
		// Restore Date objects
		return messages.map((m) => ({
			...m,
			timestamp: new Date(m.timestamp),
		}));
	} catch {
		return [];
	}
}

/**
 * Clear conversation from localStorage
 */
export function clearConversation(modelConfigId: string): void {
	try {
		const key = `${STORAGE_KEY_PREFIX}${modelConfigId}`;
		localStorage.removeItem(key);
	} catch {
		// Ignore errors
	}
}

/**
 * List all saved conversation keys
 */
export function listSavedConversations(): string[] {
	const keys: string[] = [];
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(STORAGE_KEY_PREFIX)) {
				keys.push(key.slice(STORAGE_KEY_PREFIX.length));
			}
		}
	} catch {
		// Ignore errors
	}
	return keys;
}
